import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { axiosApi } from "./axios";
import {
  CountsValidatedSummaryBo,
  DynamicCountsBo,
  ExpeditionFinalizedDayCountsBo,
  StaticCountsBo,
  CreateCustomQuotationDto,
  CreateQuotationBoDto,
  CreateQuotationDto,
  CreateReCustomQuotationBoDto,
  ExpeditionEntity,
  GetAllExpeditionBoDto,
  GetAllExpeditionBoResponseDto,
  GetAllExpeditionDto,
  GetAllExpeditionResponseDto,
  GetCountAllResponseDto,
  GetExpeditionStatsResponseDto,
  GetOneExpeditionBoResponseDto,
  GetOneExpeditionResponseDto,
  PostponeExpeditionBoDto,
  PreFinalizeQuotationFedexApiDto,
  UpdateCustomQuotationQuoteBoDto,
  UpdatePendingCustomQuotationDto,
  UpdatePendingQuotationDto,
  UpdateExpeditionBoDto,
  UpdateRequestDto,
  UpdateReturnDto,
  UpdateValidatedQuotationDto,
  UpdateValidatedQuotationImportDto,
  ValidateCustomQuotationDto,
  ValidateQuotationDto,
  ValidateQuotationImportDto,
  UpdatePreFinalizeQuotationApiDto,
  PreFinalizeQuotationApiDto,
  FinalizeQuotationApiDto,
  UpdateFinalizeQuotationApiDto,
  UpdateFinalizedQuotationDto,
  UpdateFinalizedCustomQuotationDto,
  UpdatePreFinalizeCustomQuotationApiDto,
  PreFinalizeCustomQuotationApiDto,
  PreFinalizeCustomQuotationSimplifiedApiDto,
  PreFinalizeCustomQuotationFedexApiDto,
  PrintShippingLabelsResponseDto,
  PrintShippingLabelsDto,
  GetPrintBatchBoDto,
  GetPrintBatchBoResponseDto,
} from "@/types/api/api-types";
import { AxiosResponse } from "axios";

//* FRONT OFFICE

//#region Get
const getAllExpedition = async (payload: GetAllExpeditionDto) => {
  return axiosApi
    .get<GetAllExpeditionResponseDto>("v1/expedition", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetAllExpedition = (payload: GetAllExpeditionDto) => {
  return useQuery({
    queryKey: ["expedition", "all", payload],
    queryFn: () => getAllExpedition(payload),
  });
};

const getExpeditionCount = async (payload: GetAllExpeditionDto) => {
  return axiosApi
    .get<number>("v1/expedition/count", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetExpeditionCount = (payload: GetAllExpeditionDto) => {
  return useQuery({
    queryKey: ["expedition", "all", "count", payload],
    queryFn: () => getExpeditionCount(payload),
    refetchInterval: 6000 * 10,
    refetchIntervalInBackground: false,
  });
};

const getExpeditionStats = async (payload: GetAllExpeditionDto) => {
  return axiosApi
    .get<GetExpeditionStatsResponseDto>("v1/expedition/stats", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetExpeditionStats = (payload: GetAllExpeditionDto) => {
  return useQuery({
    queryKey: ["expedition", "stats", payload],
    queryFn: () => getExpeditionStats(payload),
  });
};

const getOneExpedition = async (id: string) => {
  return axiosApi.get<GetOneExpeditionResponseDto>(`v1/expedition/${id}`).then((res) => res.data);
};

export const useGetOneExpedition = (id: string) => {
  return useQuery({
    queryKey: ["expedition", id],
    queryFn: () => getOneExpedition(id),
    enabled: !!id,
    gcTime: 0,
    staleTime: 0,
  });
};
//#endregion

//#region Post
const createQuotation = async (payload: CreateQuotationDto) => {
  return axiosApi.post<any>("v1/expedition/quotation", payload);
};

export const useCreateQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createQuotation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      toast.success(t("expeditionToasts.createQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.createQuotationError"));
    },
  });
};

const createCustomQuotation = async (payload: CreateCustomQuotationDto) => {
  return axiosApi.post<any>("v1/expedition/custom-quotation", payload);
};

export const useCreateCustomQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCustomQuotation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      toast.success(t("expeditionToasts.createCustomQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.createCustomQuotationError"));
    },
  });
};
//#endregion

//#region Patch
const validateQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: ValidateQuotationDto;
}) => {
  return axiosApi.patch<ExpeditionEntity>(`v1/expedition/quotation/validate/${id}`, payload);
};

export const useValidateQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: validateQuotation,
    onSuccess: (expedition) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      queryClient.invalidateQueries({
        queryKey: ["expedition", expedition.data.id],
      });
      toast.success(t("expeditionToasts.validateQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.validateQuotationError"));
    },
  });
};

const validateQuotationImport = async ({
  id,
  payload,
}: {
  id: string;
  payload: ValidateQuotationImportDto;
}) => {
  return axiosApi.patch<ExpeditionEntity>(`v1/expedition/quotation/validate-import/${id}`, payload);
};

export const useValidateQuotationImport = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: validateQuotationImport,
    onSuccess: (expedition) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      queryClient.invalidateQueries({
        queryKey: ["expedition", expedition.data.id],
      });
      toast.success(t("expeditionToasts.validateQuotationImportSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.validateQuotationImportError"));
    },
  });
};

const updateValidatedQuotationImport = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateValidatedQuotationImportDto;
}) => {
  return axiosApi.patch<ExpeditionEntity>(
    `v1/expedition/quotation/validated-import/${id}`,
    payload,
  );
};

export const useUpdateValidatedQuotationImport = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateValidatedQuotationImport,
    onSuccess: (res: AxiosResponse<any>) => {
      if (res.data === "No data to update") {
        toast.info(t("expeditionToasts.noDataToUpdate"));
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      queryClient.invalidateQueries({
        queryKey: ["expedition", res.data.id],
      });
      toast.success(t("expeditionToasts.updateValidatedQuotationImportSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateValidatedQuotationImportError"));
    },
  });
};

const updateValidatedQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateValidatedQuotationDto;
}) => {
  return axiosApi.patch<ExpeditionEntity>(`v1/expedition/quotation/validated/${id}`, payload);
};

export const useUpdateValidatedQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateValidatedQuotation,
    onSuccess: (res: AxiosResponse<any>) => {
      if (res.data === "No data to update") {
        toast.info(t("expeditionToasts.noDataToUpdate"));
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      queryClient.invalidateQueries({
        queryKey: ["expedition", res.data.id],
      });
      toast.success(t("expeditionToasts.updateValidatedQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateValidatedQuotationError"));
    },
  });
};

const validateCustomQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: ValidateCustomQuotationDto;
}) => {
  return axiosApi.patch<any>(`v1/expedition/custom-quotation/validate/${id}`, payload);
};

export const useValidateCustomQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: validateCustomQuotation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      toast.success(t("expeditionToasts.validateCustomQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.validateCustomQuotationError"));
    },
  });
};

const updatePendingCustomQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdatePendingCustomQuotationDto;
}) => {
  return axiosApi.patch<any>(`v1/expedition/custom-quotation/pending/${id}`, payload);
};

export const useUpdatePendingCustomQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePendingCustomQuotation,
    onSuccess: (res: AxiosResponse) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      if (res.data === "No data to update") {
        toast.info(t("expeditionToasts.noDataToUpdate"));
      } else {
        queryClient.invalidateQueries({
          queryKey: ["expedition", res.data.id],
        });
        toast.success(t("expeditionToasts.updatePendingCustomQuotationSuccess"));
      }
    },
    onError: () => {
      toast.error(t("expeditionToasts.updatePendingCustomQuotationError"));
    },
  });
};

const updatePendingQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdatePendingQuotationDto;
}) => {
  return axiosApi.patch<any>(`v1/expedition/quotation/pending/${id}`, payload);
};

export const useUpdatePendingQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePendingQuotation,
    onSuccess: (res: AxiosResponse) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      if (res.data === "No data to update") {
        toast.info(t("expeditionToasts.noDataToUpdate"));
      } else {
        queryClient.invalidateQueries({
          queryKey: ["expedition", res.data.id],
        });
        toast.success(t("expeditionToasts.updatePendingQuotationSuccess"));
      }
    },
    onError: () => {
      toast.error(t("expeditionToasts.updatePendingQuotationError"));
    },
  });
};

const updateRequest = async ({ id, payload }: { id: string; payload: UpdateRequestDto }) => {
  return axiosApi.patch<any>(`v1/expedition/update-request/${id}`, payload);
};

export const useUpdateRequest = () => {
  const { t } = useTranslation("boExpeditions");
  return useMutation({
    mutationFn: updateRequest,
    onSuccess: () => {
      toast.success(t("expeditionToasts.updateRequestSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateRequestError"));
    },
  });
};

const reassignExpeditionCreatedBy = async ({ id, userId }: { id: string; userId: string }) => {
  return axiosApi.patch<any>(`v1/expedition/reassign-created-by/${id}`, {
    userId,
  });
};

export const useReassignExpeditionCreatedBy = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reassignExpeditionCreatedBy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition"] });
      toast.success(t("expeditionToasts.reassignExpeditionCreatedBySuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.reassignExpeditionCreatedByError"));
    },
  });
};

const assignExpeditions = async ({
  expeditionIds,
  userId,
  notify,
}: {
  expeditionIds: string[];
  userId?: string;
  notify?: boolean;
}) => {
  return axiosApi.patch<any>(`v1/expedition/bo/assign`, { expeditionIds, userId, notify });
};

export const useAssignExpeditions = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assignExpeditions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition"] });
      toast.success(t("expeditionToasts.assignExpeditionsSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.assignExpeditionsError"));
    },
  });
};
//#endregion

//#region Delete
const deleteExpedition = async (id: string) => {
  return axiosApi.delete<any>(`v1/expedition/${id}`);
};

export const useDeleteExpedition = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteExpedition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      toast.success(t("expeditionToasts.deleteExpeditionSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.deleteExpeditionError"));
    },
  });
};

const deleteRequest = async (id: string) => {
  return axiosApi.post<any>(`v1/expedition/delete-request/${id}`);
};

export const useDeleteRequest = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      toast.success(t("expeditionToasts.deleteRequestSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.deleteRequestError"));
    },
  });
};

const cancelDeleteRequest = async (id: string) => {
  return axiosApi.post<any>(`v1/expedition/cancel-delete-request/${id}`);
};

export const useCancelDeleteRequest = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelDeleteRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all"] });
      toast.success(t("expeditionToasts.cancelDeleteRequestSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.cancelDeleteRequestError"));
    },
  });
};
//#endregion

//* BACK OFFICE

//#region get
const getExpeditionCountAll = async () => {
  return axiosApi.get<GetCountAllResponseDto>("v1/expedition/bo/countAll").then((res) => res.data);
};

export const useGetExpeditionCountAll = () => {
  return useQuery({
    queryKey: ["expedition", "all", "bo", "count"],
    queryFn: () => getExpeditionCountAll(),
    refetchInterval: 6000 * 10,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });
};

const getAllExpeditionBo = async (payload: GetAllExpeditionBoDto) => {
  return axiosApi
    .get<GetAllExpeditionBoResponseDto>("v1/expedition/bo", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetAllExpeditionBo = (payload: GetAllExpeditionBoDto) => {
  return useQuery({
    queryKey: ["expedition", "all", "bo", payload],
    queryFn: () => getAllExpeditionBo(payload),
  });
};

const getExpeditionCountBo = async (payload: GetAllExpeditionBoDto) => {
  return axiosApi
    .get<number>("v1/expedition/bo/count", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetExpeditionCountWoRefetchBo = (payload: GetAllExpeditionBoDto) => {
  return useQuery({
    queryKey: ["expedition", "all", "bo", "count", payload],
    queryFn: () => getExpeditionCountBo(payload),
  });
};

const getExpeditionStaticCountsBo = async (payload: GetAllExpeditionBoDto) => {
  return axiosApi
    .get<StaticCountsBo>("v1/expedition/bo/static-counts", { params: payload })
    .then((res) => res.data);
};

export const useGetExpeditionStaticCountsBo = (payload: GetAllExpeditionBoDto) => {
  return useQuery({
    queryKey: ["expedition", "bo", "static-counts", payload],
    queryFn: () => getExpeditionStaticCountsBo(payload),
  });
};

const getExpeditionDynamicCountsBo = async (payload: GetAllExpeditionBoDto) => {
  return axiosApi
    .get<DynamicCountsBo>("v1/expedition/bo/dynamic-counts", { params: payload })
    .then((res) => res.data);
};

export const useGetExpeditionDynamicCountsBo = (payload: GetAllExpeditionBoDto) => {
  return useQuery({
    queryKey: ["expedition", "bo", "dynamic-counts", payload],
    queryFn: () => getExpeditionDynamicCountsBo(payload),
  });
};

const getExpeditionCountsValidatedSummaryBo = async (payload: GetAllExpeditionBoDto) => {
  return axiosApi
    .get<CountsValidatedSummaryBo>("v1/expedition/bo/counts-summary-validated", { params: payload })
    .then((res) => res.data);
};

export const useGetExpeditionCountsValidatedSummaryBo = (payload: GetAllExpeditionBoDto) => {
  return useQuery({
    queryKey: ["expedition", "bo", "counts-summary-validated", payload],
    queryFn: () => getExpeditionCountsValidatedSummaryBo(payload),
  });
};

const getExpeditionFinalizedDayCountsBo = async (payload: GetAllExpeditionBoDto) => {
  return axiosApi
    .get<ExpeditionFinalizedDayCountsBo>("v1/expedition/bo/finalized-day-counts", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetExpeditionFinalizedDayCountsBo = (payload: GetAllExpeditionBoDto) => {
  return useQuery({
    queryKey: ["expedition", "bo", "finalized-day-counts", payload],
    queryFn: () => getExpeditionFinalizedDayCountsBo(payload),
    staleTime: 5 * 60 * 1000,
  });
};

const getExpeditionStatsBo = async ({
  id,
  payload,
}: {
  id: string;
  payload: GetAllExpeditionDto;
}) => {
  return axiosApi
    .get<GetExpeditionStatsResponseDto>(`v1/expedition/bo/stats/${id}`, {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetExpeditionStatsBo = ({
  id,
  payload,
}: {
  id: string;
  payload: GetAllExpeditionDto;
}) => {
  return useQuery({
    queryKey: ["expedition", "stats", "bo", id, payload],
    queryFn: () => getExpeditionStatsBo({ id, payload }),
  });
};

const getOneExpeditionBo = async (id: string) => {
  return axiosApi
    .get<GetOneExpeditionBoResponseDto>(`v1/expedition/bo/${id}`)
    .then((res) => res.data);
};

export const useGetOneExpeditionBo = (id: string) => {
  return useQuery({
    queryKey: ["expedition", "bo", id],
    queryFn: () => getOneExpeditionBo(id),
    enabled: !!id,
    gcTime: 0,
    staleTime: 0,
  });
};

const getPrintBatchBo = async (payload: GetPrintBatchBoDto) => {
  return axiosApi
    .get<GetPrintBatchBoResponseDto>("v1/expedition/bo/print-batch", { params: payload })
    .then((res) => res.data);
};

export const useGetPrintBatchBo = (payload: GetPrintBatchBoDto, enabled = true) => {
  return useQuery({
    queryKey: ["expedition", "bo", "print-batch", payload],
    queryFn: () => getPrintBatchBo(payload),
    enabled,
  });
};
//#endregion

//#region patch
const createQuotationBo = async (payload: CreateQuotationBoDto) => {
  return axiosApi.post<{ id: string }>(`v1/expedition/bo/quotation`, payload);
};

export const useCreateQuotationBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createQuotationBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.createQuotationBoSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.createQuotationBoError"));
    },
  });
};

const createReCustomQuotationBo = async (payload: CreateReCustomQuotationBoDto) => {
  return axiosApi.post(`v1/expedition/bo/re-custom-quotation`, payload);
};

export const useCreateReCustomQuotationBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createReCustomQuotationBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.createReCustomQuotationBoSuccess"));
    },
  });
};

const updateCustomQuotationQuoteBo = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateCustomQuotationQuoteBoDto;
}) => {
  return axiosApi.patch<{ id: string }>(`v1/expedition/bo/custom-quotation/quote/${id}`, payload);
};

export const useUpdateCustomQuotationQuoteBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomQuotationQuoteBo,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      queryClient.invalidateQueries({
        queryKey: ["expedition", "bo", res.data.id],
      });
      toast.success(t("expeditionToasts.updateCustomQuotationQuoteBoSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateCustomQuotationQuoteBoError"));
    },
  });
};

const updatePreFinalizeQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdatePreFinalizeQuotationApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(
    `v1/expedition/bo/quotation/update-pre-finalize/${id}`,
    payload,
  );
};

export const useUpdatePreFinalizeQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePreFinalizeQuotation,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.updatePreFinalizeQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updatePreFinalizeQuotationError"));
    },
  });
};

const updatePreFinalizeCustomQuotationBo = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdatePreFinalizeCustomQuotationApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(
    `v1/expedition/bo/custom-quotation/update-pre-finalize/${id}`,
    payload,
  );
};

export const useUpdatePreFinalizeCustomQuotationBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePreFinalizeCustomQuotationBo,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.updatePreFinalizeCustomQuotationBoSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updatePreFinalizeCustomQuotationBoError"));
    },
  });
};

const preFinalizeFedexQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: PreFinalizeQuotationFedexApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(
    `v1/expedition/bo/quotation/pre-finalize-fedex/${id}`,
    payload,
  );
};

export const usePreFinalizeFedexQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: preFinalizeFedexQuotation,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.preFinalizeFedexQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.preFinalizeFedexQuotationError"));
    },
  });
};

const preFinalizeQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: PreFinalizeQuotationApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(`v1/expedition/bo/quotation/pre-finalize/${id}`, payload);
};

export const usePreFinalizeQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: preFinalizeQuotation,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.preFinalizeQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.preFinalizeQuotationError"));
    },
  });
};

const preFinalizeCustomQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: PreFinalizeCustomQuotationApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(
    `v1/expedition/bo/custom-quotation/pre-finalize/${id}`,
    payload,
  );
};

export const usePreFinalizeCustomQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: preFinalizeCustomQuotation,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.preFinalizeCustomQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.preFinalizeCustomQuotationError"));
    },
  });
};

const preFinalizeCustomQuotationSimplified = async ({
  id,
  payload,
}: {
  id: string;
  payload: PreFinalizeCustomQuotationSimplifiedApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(
    `v1/expedition/bo/custom-quotation/pre-finalize-simplified/${id}`,
    payload,
  );
};

export const usePreFinalizeCustomQuotationSimplified = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: preFinalizeCustomQuotationSimplified,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.preFinalizeCustomQuotationSimplifiedSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.preFinalizeCustomQuotationSimplifiedError"));
    },
  });
};

const preFinalizeCustomQuotationFedex = async ({
  id,
  payload,
}: {
  id: string;
  payload: PreFinalizeCustomQuotationFedexApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(
    `v1/expedition/bo/custom-quotation/pre-finalize-fedex/${id}`,
    payload,
  );
};

export const usePreFinalizeCustomQuotationFedex = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: preFinalizeCustomQuotationFedex,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.preFinalizeCustomQuotationFedexSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.preFinalizeCustomQuotationFedexError"));
    },
  });
};

const updateFinalizeQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateFinalizeQuotationApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(
    `v1/expedition/bo/quotation/update-finalize/${id}`,
    payload,
  );
};

export const useUpdateFinalizeQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateFinalizeQuotation,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.updateFinalizeQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateFinalizeQuotationError"));
    },
  });
};

const finalizeQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: FinalizeQuotationApiDto;
}) => {
  return axiosApi.patch<{ id: string }>(`v1/expedition/bo/quotation/finalize/${id}`, payload);
};

export const useFinalizeQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: finalizeQuotation,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({
        queryKey: ["expedition", "bo", res.data.id],
      });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.finalizeQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.finalizeQuotationError"));
    },
  });
};

const cancelExpedition = async ({ id }: { id: string }) => {
  return axiosApi.patch<{ id: string }>(`v1/expedition/bo/expedition/cancel/${id}`);
};

export const useCancelExpedition = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelExpedition,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      queryClient.invalidateQueries({
        queryKey: ["expedition", "bo", res.data.id],
      });
      toast.success(t("expeditionToasts.cancelExpeditionSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.cancelExpeditionError"));
    },
  });
};

const updatedFinalizedQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateFinalizedQuotationDto;
}) => {
  return axiosApi.patch<{ id: string }>(`v1/expedition/bo/finalized-quotation/${id}`, payload);
};

export const useUpdateFinalizedQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatedFinalizedQuotation,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.updateFinalizedQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateFinalizedQuotationError"));
    },
  });
};

const updatedFinalizedCustomQuotation = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateFinalizedCustomQuotationDto;
}) => {
  return axiosApi.patch<{ id: string }>(
    `v1/expedition/bo/finalized-custom-quotation/${id}`,
    payload,
  );
};

export const useUpdateFinalizedCustomQuotation = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatedFinalizedCustomQuotation,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.id] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.updateFinalizedCustomQuotationSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateFinalizedCustomQuotationError"));
    },
  });
};

const updateExpeditionBo = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateExpeditionBoDto;
}) => {
  return axiosApi.patch<{ id: string }>(`v1/expedition/bo/expedition/${id}`, payload);
};

export const useUpdateExpeditionBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateExpeditionBo,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      queryClient.invalidateQueries({
        queryKey: ["expedition", "bo", res.data.id],
      });
      toast.success(t("expeditionToasts.updateExpeditionBoSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateExpeditionBoError"));
    },
  });
};

const postponeExpeditionBo = async ({
  id,
  payload,
}: {
  id: string;
  payload: PostponeExpeditionBoDto;
}) => {
  return axiosApi.patch<{ id: string }>(`v1/expedition/bo/postpone/${id}`, payload);
};

export const usePostponeExpeditionBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postponeExpeditionBo,
    onSuccess: (res: AxiosResponse<{ id: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      queryClient.invalidateQueries({
        queryKey: ["expedition", "bo", res.data.id],
      });
      toast.success(t("expeditionToasts.postponeExpeditionBoSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.postponeExpeditionBoError"));
    },
  });
};

const recoverExpedition = async (id: string) => {
  return axiosApi.patch<any>(`v1/expedition/bo/recover/${id}`);
};

export const useRecoverExpedition = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recoverExpedition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.recoverExpeditionSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.recoverExpeditionError"));
    },
  });
};

const updateReturnBo = async ({ id, payload }: { id: string; payload: UpdateReturnDto }) => {
  return axiosApi.patch<any>(`v1/expedition/bo/return/${id}`, payload);
};

export const useUpdateReturnBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateReturnBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.updateReturnBoSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.updateReturnBoError"));
    },
  });
};

const printShippingLabels = async (payload: PrintShippingLabelsDto) => {
  return axiosApi
    .patch<PrintShippingLabelsResponseDto>("v1/expedition/bo/print-shipping-labels", payload)
    .then((res) => res.data);
};

export const usePrintShippingLabels = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: printShippingLabels,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition"] });
    },
    onError: () => {
      toast.error(t("expeditionToasts.printShippingLabelsError"));
    },
  });
};
//#endregion

//#region Delete
const deleteExpeditionBo = async (id: string) => {
  return axiosApi.delete<any>(`v1/expedition/bo/${id}`);
};

export const useDeleteExpeditionBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteExpeditionBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.deleteExpeditionBoSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.deleteExpeditionBoError"));
    },
  });
};

const permanentDeleteExpeditionBo = async (id: string) => {
  return axiosApi.delete<any>(`v1/expedition/bo/permanent-delete/${id}`);
};

export const usePermanentDeleteExpeditionBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: permanentDeleteExpeditionBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.permanentDeleteExpeditionBoSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.permanentDeleteExpeditionBoError"));
    },
  });
};

const resetDraftOwner = async (id: string) => {
  return axiosApi.patch(`v1/expedition/bo/draft/${id}/reset-owner`);
};

export const useResetDraftOwner = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetDraftOwner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      toast.success(t("expeditionToasts.resetDraftOwnerSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.resetDraftOwnerError"));
    },
  });
};

const markDhlTntShipped = async (id: string) => {
  return axiosApi.patch(`v1/expedition/bo/mark-dhl-tnt-shipped/${id}`);
};

export const useMarkDhlTntShipped = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markDhlTntShipped,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expedition"] });
      toast.success(t("expeditionToasts.markDhlTntShippedSuccess"));
    },
    onError: () => {
      toast.error(t("expeditionToasts.markDhlTntShippedError"));
    },
  });
};
//#endregion

//#endregion
